import { useEffect, useMemo, useState } from 'react';
import { CircleHelp, FileSearch, Save, Settings2, X } from 'lucide-react';
import { getApiErrorMessage, listLocalidadesOperativas, listSucursalesOperativas } from '@/services/sucursalesOperativas.js';
import SearchableSelect from './SearchableSelect.jsx';
import CollapsibleSection from './CollapsibleSection.jsx';
import RuleScopeFields from './RuleScopeFields.jsx';
import RuleCalculationFields from './RuleCalculationFields.jsx';
import RuleSummaryPanel from './RuleSummaryPanel.jsx';
import { buttonClass, fieldClass, LabeledField, money, primaryButtonClass, shellCard } from './shared.jsx';

const baseOptions = [
  { value: 'GUIA', label: 'Guía' },
  { value: 'KG', label: 'Peso' },
  { value: 'BULTO', label: 'Bultos' },
  { value: 'M3', label: 'Metros cúbicos' },
  { value: 'KILO_AFORADO', label: 'Kilo aforado' },
  { value: 'VALOR_DECLARADO', label: 'Valor declarado' },
  { value: 'CONTRAREEMBOLSO', label: 'Contrareembolso' },
];

const methodOptions = [
  { value: 'FIJO', label: 'Fijo' },
  { value: 'POR_UNIDAD', label: 'Por unidad' },
  { value: 'PORCENTAJE', label: 'Porcentaje' },
];

const helpBySection = {
  contexto: {
    title: 'Contexto de la regla',
    body: [
      'La regla vive dentro de una lista y una versión. Primero se congela el contexto comercial y después se arma la excepción.',
      'La prioridad sólo desempata cuando dos reglas ya empataron en especificidad.',
    ],
  },
  alcance: {
    title: 'Dónde aplica',
    body: [
      'Cliente, servicio, numerador, sucursal y localidad forman el universo operativo real de la regla.',
      'Mientras más filtros cargues, más específica será la regla.',
    ],
  },
  calculo: {
    title: 'Cómo calcula',
    body: [
      'Base + método tienen que leerse como una instrucción breve de negocio.',
      'El mínimo evita importes demasiado bajos y el rango permite escalones.',
    ],
  },
};

function CompactSection({ title, description, children, className = '' }) {
  return (
    <section className={`rounded-2xl border border-[rgba(var(--border),0.35)] bg-[rgba(var(--subpanel),0.14)] p-4 ${className}`}>
      <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">{title}</h3>
      <p className="mt-1 text-xs text-[rgba(var(--muted),0.95)]">{description}</p>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function buildCalculationSentence(form) {
  const baseLabel = baseOptions.find((item) => item.value === form.unidadBase)?.label || 'la base elegida';
  const methodLabel = methodOptions.find((item) => item.value === form.metodoCalculo)?.label || 'el método elegido';

  if (!form.unidadBase || !form.metodoCalculo) return 'Definí base y método para ver una lectura operativa del cálculo.';
  if (form.metodoCalculo === 'POR_UNIDAD') return `Se cobrará ${money(form.valorUnitario)} por cada ${baseLabel.toLowerCase()}, con mínimo ${money(form.minimo)}.`;
  if (form.metodoCalculo === 'PORCENTAJE') return `Se aplicará ${Number(form.porcentaje || 0)}% sobre ${baseLabel.toLowerCase()}, con mínimo ${money(form.minimo)}.`;
  return `Se cobrará un fijo por guía de ${money(form.valorUnitario || 0)}, con mínimo ${money(form.minimo)} si corresponde.`;
}

function buildWarnings(form, clientMode) {
  return [
    !form.versionId ? 'Falta seleccionar la lista / versión.' : null,
    !form.conceptId ? 'Falta seleccionar el concepto.' : null,
    clientMode === 'specific' && !form.clienteId ? 'El modo cliente específico requiere elegir un cliente.' : null,
    !form.unidadBase || !form.metodoCalculo ? 'El cálculo todavía no está completo.' : null,
    form.origenSucursalId && !form.origenLocalidadId ? 'Hay sucursal de origen, pero no localidad de origen.' : null,
    form.destinoSucursalId && !form.destinoLocalidadId ? 'Hay sucursal de destino, pero no localidad de destino.' : null,
  ].filter(Boolean);
}

function buildStatus(form, isDirty) {
  if (isDirty) return 'Con cambios sin guardar';
  if (form.id) return 'Guardado';
  return 'Borrador';
}

function normalizeSucursalOptions(rows) {
  return rows.map((item) => ({
    value: item.id,
    label: `${item.numero_sucursal || item.codigo || item.id} · ${item.nombre || 'Sucursal'}`,
    provinciaId: item.provincia_id,
    localidadId: item.localidad_id,
  }));
}

function normalizeLocalidadOptions(rows) {
  return rows.map((item) => ({
    value: item.id,
    label: item.nombre || item.localidad_nombre || `Localidad ${item.id}`,
    provinciaId: item.provincia_id || item.provinciaId || null,
  }));
}

export default function RuleForm({
  form,
  concepts,
  numerators,
  listOptions,
  previewText,
  selectedCliente,
  clientMode,
  onChange,
  onClienteSelect,
  onClientModeChange,
  onCancel,
  onSubmit,
  onSimulate,
  onHistory,
  onToggleMode,
  isSubmitting,
  disableSubmit,
  errors,
  isDirty = false,
  assistantPanel = null,
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpSection, setHelpSection] = useState('contexto');
  const [showObservations, setShowObservations] = useState(Boolean(form.observaciones));
  const [showPro, setShowPro] = useState(Boolean(form.observaciones));
  const [catalogError, setCatalogError] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [localidades, setLocalidades] = useState([]);

  const activeVersionLabel = useMemo(() => listOptions.find((item) => String(item.value) === String(form.versionId))?.label || '', [form.versionId, listOptions]);
  const activeConceptLabel = useMemo(() => {
    const concept = concepts.find((item) => String(item.id) === String(form.conceptId));
    return concept ? `${concept.codigo} · ${concept.nombre}` : '';
  }, [concepts, form.conceptId]);

  const serviceOptions = useMemo(() => {
    const services = new Set();
    if (form.servicioCodigo) services.add(form.servicioCodigo);
    return Array.from(services).map((item) => ({ value: item, label: item }));
  }, [form.servicioCodigo]);

  const sucursalOptions = useMemo(() => normalizeSucursalOptions(sucursales), [sucursales]);
  const localidadOptions = useMemo(() => normalizeLocalidadOptions(localidades), [localidades]);
  const selectedOrigenSucursal = useMemo(() => sucursalOptions.find((item) => String(item.value) === String(form.origenSucursalId)), [form.origenSucursalId, sucursalOptions]);
  const selectedDestinoSucursal = useMemo(() => sucursalOptions.find((item) => String(item.value) === String(form.destinoSucursalId)), [form.destinoSucursalId, sucursalOptions]);

  const origenLocalidadOptions = useMemo(() => {
    if (!selectedOrigenSucursal?.provinciaId) return localidadOptions;
    return localidadOptions.filter((item) => String(item.provinciaId) === String(selectedOrigenSucursal.provinciaId) || String(item.value) === String(selectedOrigenSucursal.localidadId));
  }, [localidadOptions, selectedOrigenSucursal]);

  const destinoLocalidadOptions = useMemo(() => {
    if (!selectedDestinoSucursal?.provinciaId) return localidadOptions;
    return localidadOptions.filter((item) => String(item.provinciaId) === String(selectedDestinoSucursal.provinciaId) || String(item.value) === String(selectedDestinoSucursal.localidadId));
  }, [localidadOptions, selectedDestinoSucursal]);

  const calculationSentence = useMemo(() => buildCalculationSentence(form), [form]);
  const warnings = useMemo(() => buildWarnings(form, clientMode), [clientMode, form]);
  const status = useMemo(() => buildStatus(form, isDirty), [form, isDirty]);
  const helpContent = helpBySection[helpSection] || helpBySection.contexto;

  useEffect(() => {
    let cancelled = false;
    Promise.all([listSucursalesOperativas(), listLocalidadesOperativas()])
      .then(([sucData, locData]) => {
        if (cancelled) return;
        setSucursales(Array.isArray(sucData) ? sucData : []);
        setLocalidades(Array.isArray(locData) ? locData : []);
        setCatalogError('');
      })
      .catch((error) => {
        if (cancelled) return;
        setCatalogError(getApiErrorMessage(error, 'No se pudieron cargar los catálogos de sucursales y localidades.'));
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'F1') {
        event.preventDefault();
        setHelpOpen(true);
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        onSubmit?.();
      }
      if (event.key === 'Escape') setHelpOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onSubmit]);

  return (
    <div className="space-y-4">
      <header className={`${shellCard} sticky top-3 z-20 px-4 py-3 backdrop-blur`}>
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.55)] px-2.5 py-1 text-[11px] font-semibold text-[rgba(var(--fg),0.86)]">
                {activeVersionLabel || 'Sin lista / versión'}
              </span>
              <span className="rounded-full border border-[rgba(var(--border),0.35)] bg-[rgba(var(--panel),0.55)] px-2.5 py-1 text-[11px] font-semibold text-[rgba(var(--fg),0.86)]">
                {activeConceptLabel || 'Sin concepto'}
              </span>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${isDirty ? 'bg-[rgba(var(--brand),0.14)] text-[rgb(var(--brand))]' : 'bg-[rgba(var(--subpanel),0.35)] text-[rgba(var(--muted),0.95)]'}`}>
                {status}
              </span>
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-3 xl:grid-cols-5">
              <div className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.4)] px-3 py-2 text-sm text-[rgb(var(--fg))]">Código: {form.codigo || 'Sin código'}</div>
              <div className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.4)] px-3 py-2 text-sm text-[rgb(var(--fg))]">Nombre: {form.nombre || 'Sin nombre'}</div>
              <div className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.4)] px-3 py-2 text-sm text-[rgb(var(--fg))]">Prioridad: {form.prioridad || 0}</div>
              <div className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.4)] px-3 py-2 text-sm text-[rgb(var(--fg))]">Base: {form.unidadBase || 'Sin base'}</div>
              <div className="rounded-xl border border-[rgba(var(--border),0.28)] bg-[rgba(var(--panel),0.4)] px-3 py-2 text-sm text-[rgb(var(--fg))]">Método: {form.metodoCalculo || 'Sin método'}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 xl:justify-end">
            <button type="button" className={primaryButtonClass} onClick={onSubmit} disabled={disableSubmit || isSubmitting}>
              <Save className="mr-2 h-4 w-4" />Guardar regla
            </button>
            <button type="button" className={buttonClass} onClick={onSimulate}>
              <FileSearch className="mr-2 h-4 w-4" />Probar esta regla
            </button>
            {onToggleMode ? (
              <button type="button" className={buttonClass} onClick={onToggleMode}>
                <Settings2 className="mr-2 h-4 w-4" />Modo simple
              </button>
            ) : null}
            <button type="button" className={buttonClass} onClick={() => setShowPro((prev) => !prev)} aria-expanded={showPro}>
              <Settings2 className="mr-2 h-4 w-4" />Ajustes pro
            </button>
            <button type="button" className={buttonClass} onClick={() => setHelpOpen(true)} aria-expanded={helpOpen}>
              <CircleHelp className="mr-2 h-4 w-4" />Ayuda
            </button>
            <button type="button" className={buttonClass} onClick={onCancel}>
              <X className="mr-2 h-4 w-4" />Cancelar
            </button>
          </div>
        </div>
      </header>

      {catalogError ? (
        <div className="rounded-2xl border border-[rgba(var(--danger),0.22)] bg-[rgba(var(--danger),0.08)] px-4 py-3 text-sm text-[rgb(var(--fg))]">
          {catalogError}
        </div>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.45fr)_340px]">
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-4">
            <CompactSection title="Contexto de la regla" description="Identidad y ubicación comercial de la regla." className="col-span-12 xl:col-span-5">
              <div className="grid grid-cols-12 gap-3">
                <LabeledField label="Lista / versión" hint="Contexto comercial." span="col-span-12">
                  <SearchableSelect
                    value={form.versionId || ''}
                    onChange={(value) => onChange('versionId', value)}
                    options={listOptions}
                    placeholder="Seleccionar versión"
                    searchPlaceholder="Buscar versión"
                    emptyMessage="Sin versiones"
                  />
                  {errors.versionId ? <span className="mt-1 block text-xs text-[rgb(var(--brand))]">{errors.versionId}</span> : null}
                </LabeledField>
                <LabeledField label="Concepto" hint="Aparece en el detalle valorizado." span="col-span-12">
                  <SearchableSelect
                    value={form.conceptId || ''}
                    onChange={(value) => onChange('conceptId', value)}
                    options={concepts.map((item) => ({ value: item.id, label: `${item.codigo} · ${item.nombre}` }))}
                    placeholder="Seleccionar concepto"
                    searchPlaceholder="Buscar concepto"
                    emptyMessage="Sin conceptos"
                  />
                  {errors.conceptId ? <span className="mt-1 block text-xs text-[rgb(var(--brand))]">{errors.conceptId}</span> : null}
                </LabeledField>
                <LabeledField label="Código" span="col-span-12 md:col-span-7">
                  <input className={fieldClass} value={form.codigo} onChange={(e) => onChange('codigo', e.target.value)} placeholder="FLETE_ROSARIO_KG" />
                </LabeledField>
                <LabeledField label="Prioridad" span="col-span-12 md:col-span-5">
                  <input type="number" className={fieldClass} value={form.prioridad} onChange={(e) => onChange('prioridad', e.target.value)} />
                </LabeledField>
                <LabeledField label="Nombre" span="col-span-12">
                  <input className={fieldClass} value={form.nombre} onChange={(e) => onChange('nombre', e.target.value)} placeholder="Flete Rosario por kilo" />
                </LabeledField>
              </div>
            </CompactSection>

            <CompactSection title="Dónde aplica" description="Filtros de alcance homogéneos y rápidos de leer." className="col-span-12 xl:col-span-7">
              <RuleScopeFields
                form={form}
                numerators={numerators}
                selectedCliente={selectedCliente}
                clientMode={clientMode}
                onChange={onChange}
                onClienteSelect={onClienteSelect}
                onClientModeChange={onClientModeChange}
                serviceOptions={serviceOptions}
                sucursalOptions={sucursalOptions}
                origenLocalidadOptions={origenLocalidadOptions}
                destinoLocalidadOptions={destinoLocalidadOptions}
              />
            </CompactSection>
          </div>

          <CompactSection title="Cómo calcula" description="Grilla densa y operativa para reducir scroll y evitar stacking innecesario.">
            <RuleCalculationFields
              form={form}
              errors={errors}
              baseOptions={baseOptions}
              methodOptions={methodOptions}
              calculationSentence={calculationSentence}
              onChange={onChange}
            />
          </CompactSection>

          <CollapsibleSection
            title="Observaciones"
            description="Colapsada por defecto para no ocupar espacio cuando no hace falta."
            open={showObservations}
            onToggle={() => setShowObservations((prev) => !prev)}
          >
            <textarea
              className={`${fieldClass} min-h-24`}
              value={form.observaciones}
              onChange={(e) => onChange('observaciones', e.target.value)}
              placeholder="Motivo operativo, excepción de negocio o aclaración para soporte y auditoría."
            />
          </CollapsibleSection>

          {showPro ? (
            <CompactSection title="Ajustes pro" description="Atajos útiles para una edición más veloz sin tocar la lógica del motor.">
              <div className="flex flex-wrap gap-2">
                <button type="button" className={buttonClass} onClick={onHistory}>Ver historial</button>
                <button type="button" className={buttonClass} onClick={() => setHelpSection('alcance')}>Ayuda de alcance</button>
                <button type="button" className={buttonClass} onClick={() => setHelpSection('calculo')}>Ayuda de cálculo</button>
              </div>
            </CompactSection>
          ) : null}
        </div>

        <RuleSummaryPanel
          status={status}
          activeVersionLabel={activeVersionLabel}
          activeConceptLabel={activeConceptLabel}
          previewText={previewText}
          warnings={warnings}
          form={form}
          onOpenHelp={() => setHelpOpen(true)}
        />

        {assistantPanel}
      </div>

      {helpOpen ? (
        <div className="fixed inset-0 z-40 flex justify-end bg-[rgba(var(--shadow),0.34)]" role="presentation" onClick={() => setHelpOpen(false)}>
          <aside
            className="h-full w-full max-w-[420px] overflow-y-auto border-l border-[rgba(var(--border),0.38)] bg-[rgba(var(--panel),0.98)] p-5 shadow-[0_18px_36px_rgba(var(--shadow),0.24)]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rule-help-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[rgba(var(--muted),0.95)]">Ayuda contextual</p>
                <h2 id="rule-help-title" className="mt-1 text-lg font-semibold text-[rgb(var(--fg))]">{helpContent.title}</h2>
              </div>
              <button type="button" className={buttonClass} onClick={() => setHelpOpen(false)}>Cerrar</button>
            </div>
            <div className="mt-4 space-y-3">
              {helpContent.body.map((item) => (
                <p key={item} className="rounded-2xl border border-[rgba(var(--border),0.3)] bg-[rgba(var(--subpanel),0.14)] p-3 text-sm leading-6 text-[rgba(var(--fg),0.86)]">
                  {item}
                </p>
              ))}
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
